import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useNavigate } from 'react-router-dom';
import SubPageHeader from '../../components/molecules/SubPageHeader';
import { Button, WarningMessage } from '../../components/widgets';
import { INTEGRATION_CARDS, STATUSES, TRANSLATE_PREFIX } from '../consts';
import IntegrationRequestService from '../service';
import IntegrationIcon from './IntegrationIcon';
import StatusBadge from './StatusBadge';
import css from './IntegrationRequestForm.css';

const service = new IntegrationRequestService();
const BASE_PATH = '/react/integrations';

const IntegrationRequestForm = ({
    type,
    requestType,
    existingRequest = null,
    readOnly = false,
    onAddRequest,
    onUpdateRequest,
    translate,
}) => {
    const navigate = useNavigate();
    const card = INTEGRATION_CARDS.find(c => c.type === type);
    const titleKey = card ? card.titleKey : '';
    const descriptionKey = card ? card.descriptionKey : '';
    const typeName = card ? translate(card.titleKey) : type;
    const status = existingRequest ? existingRequest.status : STATUSES.NOT_STARTED;

    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const getFormData = () => ({});

    const ensureDraft = async formData => {
        if (existingRequest) {
            const updated = await service.updateIntegrationRequest(existingRequest.id, { formData });
            onUpdateRequest(updated);
            return updated;
        }
        const created = await service.createIntegrationRequest({
            requestType,
            formData,
        });
        onAddRequest(created);
        return created;
    };

    const handleSaveDraft = async () => {
        setSaving(true);
        setError(null);
        try {
            const formData = getFormData();
            await ensureDraft(formData);
            navigate(BASE_PATH, {
                state: {
                    successMessage: { key: `${TRANSLATE_PREFIX}.MESSAGES.DRAFT_SAVED`, typeKey: titleKey },
                },
            });
        } catch {
            setError(`${TRANSLATE_PREFIX}.ERRORS.SAVE_DRAFT`);
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const formData = getFormData();
            const draft = await ensureDraft(formData);
            const submitted = await service.submitIntegrationRequest(draft.id);
            onUpdateRequest(submitted);
            navigate(BASE_PATH, {
                state: {
                    successMessage: { key: `${TRANSLATE_PREFIX}.MESSAGES.SUBMITTED`, typeKey: titleKey },
                },
            });
        } catch {
            setError(`${TRANSLATE_PREFIX}.ERRORS.SUBMIT_REQUEST`);
        } finally {
            setSubmitting(false);
        }
    };

    const isBusy = saving || submitting;

    return (
        <div>
            <SubPageHeader
                onGoBack={() => navigate(BASE_PATH)}
                goBackTranslationKey={`${TRANSLATE_PREFIX}.ACTIONS.GO_BACK`}
            />

            {error && <WarningMessage type="error" message={<Translate id={error} />} className={css.banner} />}

            <div className={css.titleSection}>
                <IntegrationIcon type={type} />
                <div className={css.titleContent}>
                    <div className={css.titleRow}>
                        <span className={css.titleName}>{typeName}</span>
                        <StatusBadge status={status} />
                    </div>
                    {descriptionKey && (
                        <div className={css.titleDescription}>
                            <Translate id={descriptionKey} />
                        </div>
                    )}
                </div>
            </div>

            <div className={css.emptyState}>
                <div className={css.emptyTitle}>
                    <Translate id={`${TRANSLATE_PREFIX}.EMPTY_STATE.TITLE`} data={{ type: typeName }} />
                </div>
                <div className={css.emptyDescription}>
                    <Translate
                        id={`${TRANSLATE_PREFIX}.EMPTY_STATE.DESCRIPTION`}
                        data={{ type: typeName.toLowerCase() }}
                    />
                </div>
            </div>
            <div className={css.actionBar}>
                {readOnly ? (
                    <Button type="primary" onClick={() => navigate(BASE_PATH)}>
                        <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.DONE`} />
                    </Button>
                ) : (
                    <>
                        {(!existingRequest || existingRequest.status === STATUSES.DRAFT) && (
                            <Button type="secondary" onClick={handleSaveDraft} disabled={isBusy}>
                                <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.${saving ? 'SAVING' : 'SAVE_DRAFT'}`} />
                            </Button>
                        )}
                        <Button type="primary" onClick={handleSubmit} disabled={isBusy}>
                            <Translate id={`${TRANSLATE_PREFIX}.ACTIONS.${submitting ? 'SUBMITTING' : 'SUBMIT'}`} />
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

IntegrationRequestForm.propTypes = {
    type: PropTypes.string.isRequired,
    requestType: PropTypes.string.isRequired,
    existingRequest: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        adnetwork_id: PropTypes.number,
        form_data: PropTypes.shape({}),
    }),
    readOnly: PropTypes.bool,
    onAddRequest: PropTypes.func.isRequired,
    onUpdateRequest: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(IntegrationRequestForm);
